// Tutorial animations — small, self-contained SVG loops that demonstrate
// each game mechanic in motion. Designed for inline use inside the base
// Tutorial modal and the per-world MechanicTutorial modals.
//
// Why a separate component instead of reusing Board.tsx in "demo mode":
//   1. Board.tsx is the most battle-tested file in the codebase. Threading
//      a demo-mode prop through it would risk regressions on real gameplay.
//   2. Tutorial visuals only need to communicate the mechanic — they don't
//      need pixel-perfect equivalence to in-game rendering. The simplified
//      visuals here actually read more clearly at modal scale (small).
//   3. Pure CSS keyframes mean zero runtime cost, no useEffect cleanup,
//      no animation library, and theme-awareness via CSS variables.
//
// Visual style mirrors the live game:
//   - Pointy-top hexes with the same TILE_PALETTE colors
//   - White-stroke charged ring on charged tiles
//   - Padlock badge on locked tiles (matching Board.tsx's lock visual)
//   - Three small dots on mixer tiles (matching Board.tsx)
//   - Arrow path on gravity cells (matching Board.tsx's gravity arrow)
//
// Each animation is a 4-5 second loop with infinite repeat. Loops are
// designed to read clearly in 1-2 cycles — a player glancing at the
// modal for 4 seconds should grok the mechanic.
//
// Sizing: hex size 22px (vs 34px in-game) so the demo fits inside a
// modal at ~260px wide. ViewBox sets the coordinate system; the SVG
// scales responsively within its container via width="100%".
import { TILE_PALETTE } from '../hex-layout';

// Hex math for tutorial scale. Pointy-top, axial coord (q, r=0 for
// horizontal rows). Spacing between adjacent hexes in a row = sqrt(3)*size.
const SIZE = 22;
const STEP = SIZE * Math.sqrt(3);
const PAD_X = SIZE * 1.2;
const PAD_Y = SIZE * 1.4;

// Cell positions for a 5-cell horizontal row, indexed 0-4.
const CELL_X = (i: number) => PAD_X + i * STEP;
const CELL_Y = PAD_Y;

function hexPath(size: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    const x = (size * Math.cos(angle)).toFixed(2);
    const y = (size * Math.sin(angle)).toFixed(2);
    pts.push((i === 0 ? 'M' : 'L') + x + ',' + y);
  }
  return pts.join(' ') + ' Z';
}

// Empty hex cell (board background). Always rendered behind tiles.
function HexCell({ i, total }: { i: number; total: number }) {
  return (
    <g transform={`translate(${CELL_X(i)},${CELL_Y})`}>
      <path
        d={hexPath(SIZE)}
        fill="var(--hex-bg, #00000008)"
        stroke="var(--hex-line, #3a3a3a)"
        strokeOpacity="0.35"
        strokeWidth="1"
      />
      {/* total used to suppress "unused i" if we ever switch sizing */}
      {total < 0 ? null : null}
    </g>
  );
}

// Common tile shape — colored hex with white inner stroke (matches game).
function TileBody({ color }: { color: string }) {
  return (
    <>
      <path d={hexPath(SIZE)} fill={color} stroke="#FFFFFF22" strokeWidth="1.5" />
      <path d={hexPath(SIZE - 6)} fill="none" stroke="#FFFFFFCC" strokeWidth="1" opacity="0.6" />
    </>
  );
}

// Charged ring overlay — a slightly inset hex outline drawn over a tile.
function ChargedRing() {
  return <path d={hexPath(SIZE - 4)} fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.95" />;
}

// Lock badge — small circle with padlock body + shackle, positioned at
// upper-right of the hex. Matches Board.tsx's lock visual at smaller scale.
function LockBadge() {
  const bx = SIZE * 0.55;
  const by = -SIZE * 0.45;
  const r = SIZE * 0.32;
  return (
    <g transform={`translate(${bx.toFixed(2)},${by.toFixed(2)})`}>
      <circle r={r} fill="#F5F2EBE8" stroke="#1C1C1E" strokeWidth="1.2" />
      {/* Shackle */}
      <path
        d={`M ${(-r * 0.45).toFixed(2)} ${(-r * 0.05).toFixed(2)} A ${(r * 0.45).toFixed(2)} ${(r * 0.45).toFixed(2)} 0 0 1 ${(r * 0.45).toFixed(2)} ${(-r * 0.05).toFixed(2)}`}
        fill="none"
        stroke="#1C1C1E"
        strokeWidth="1.2"
      />
      {/* Body */}
      <rect x={-r * 0.55} y={-r * 0.05} width={r * 1.1} height={r * 0.7} rx={r * 0.12} fill="#1C1C1E" />
      {/* Keyhole dot */}
      <circle cx="0" cy={r * 0.22} r={r * 0.13} fill="#F5F2EBE8" />
    </g>
  );
}

// Mixer dots — three small colored circles in a triangle. Matches Board.tsx.
function MixerDots() {
  return (
    <>
      <circle cx={0} cy={-SIZE * 0.22} r={SIZE * 0.11} fill="#C7613A" opacity="0.9" />
      <circle cx={-SIZE * 0.20} cy={SIZE * 0.10} r={SIZE * 0.11} fill="#3F6F8B" opacity="0.9" />
      <circle cx={SIZE * 0.20} cy={SIZE * 0.10} r={SIZE * 0.11} fill="#5E7A58" opacity="0.9" />
    </>
  );
}

// Eastward gravity arrow drawn into a cell. Same visual treatment as the
// in-game arrow but smaller. Used in the W14 demo only.
function GravityArrowEast() {
  const len = SIZE * 0.7;
  return (
    <g opacity="0.55">
      <line x1={-len * 0.5} y1={0} x2={len * 0.5} y2={0} stroke="var(--hex-line, #3a3a3a)" strokeWidth="2" strokeLinecap="round" />
      <polyline
        points={`${(len * 0.2).toFixed(2)},${(-len * 0.35).toFixed(2)} ${(len * 0.5).toFixed(2)},0 ${(len * 0.2).toFixed(2)},${(len * 0.35).toFixed(2)}`}
        fill="none"
        stroke="var(--hex-line, #3a3a3a)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

// Subtle "tap" indicator drawn under the demo. Concentric expanding ring
// gives a light visual hint at the slide moment without being noisy.
function TapPulse({ x, y, animClass }: { x: number; y: number; animClass: string }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle r={SIZE * 0.45} fill="none" stroke="var(--accent, #C7613A)" strokeWidth="1.5" className={animClass} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Demo: BASE — "Every tile in the path slides, not just the one you tapped"
// ---------------------------------------------------------------------------
//
// Layout: 5 cells in a row. Three differently-colored tiles at slots 0, 2, 4.
// Player taps the leftmost tile and chooses east. ALL tiles in the row
// slide east until the rightmost hits the wall — ending at slots 2, 3, 4.
//
// The novel concept this teaches: it's not single-tile movement. The whole
// row participates in one slide. New players reading the text alone often
// miss this and try to "tap a tile to swap with neighbor" — that's a
// different game (Bejeweled). Showing it once removes that confusion.
const BASE_CSS = `
.tess-anim-base .t { transform-origin: center; animation: 4s ease-in-out infinite; }
.tess-anim-base .t-a { animation-name: tess-base-a; }
.tess-anim-base .t-b { animation-name: tess-base-b; }
.tess-anim-base .t-c { animation-name: tess-base-c; }
.tess-anim-base .pulse { animation: 4s ease-in-out infinite tess-base-pulse; transform-origin: center; opacity: 0; }

@keyframes tess-base-a { /* slot 0 → slot 2 */
  0%, 18% { transform: translateX(0); }
  42%, 70% { transform: translateX(${(2 * STEP).toFixed(2)}px); }
  85%, 100% { transform: translateX(0); }
}
@keyframes tess-base-b { /* slot 2 → slot 3 */
  0%, 18% { transform: translateX(0); }
  42%, 70% { transform: translateX(${STEP.toFixed(2)}px); }
  85%, 100% { transform: translateX(0); }
}
@keyframes tess-base-c { /* slot 4 stays at 4 (already at wall) */
  0%, 100% { transform: translateX(0); }
}
@keyframes tess-base-pulse {
  0%, 14% { opacity: 0; r: ${(SIZE * 0.45).toFixed(2)}; }
  20%     { opacity: 0.9; r: ${(SIZE * 0.45).toFixed(2)}; }
  34%     { opacity: 0; r: ${(SIZE * 0.85).toFixed(2)}; }
  100%    { opacity: 0; }
}
`;

export function BaseSlideDemo() {
  const w = PAD_X * 2 + 4 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-base"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing how a slide moves every tile in the row, not just the one tapped"
      style={{ display: 'block', maxWidth: 320, margin: '4px auto 12px' }}
    >
      <style>{BASE_CSS}</style>
      {[0, 1, 2, 3, 4].map((i) => <HexCell key={i} i={i} total={5} />)}
      {/* Tap pulse on the leftmost tile, prompting the player's eye */}
      <TapPulse x={CELL_X(0)} y={CELL_Y} animClass="pulse" />
      {/* Tile A — starts at slot 0, ends at slot 2 */}
      <g className="t t-a" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
      {/* Tile B — starts at slot 2, ends at slot 3 */}
      <g className="t t-b" transform={`translate(${CELL_X(2)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[1]!} />
      </g>
      {/* Tile C — stays at slot 4 (against right wall) */}
      <g className="t t-c" transform={`translate(${CELL_X(4)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[3]!} />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W11 KEEP — "Charged tile breaks the lock"
// ---------------------------------------------------------------------------
//
// Layout: 4 cells. Charged red tile at slot 0, locked red tile at slot 3.
// The charged red slides east, ends at slot 2 (adjacent to the lock). The
// padlock badge dissolves; the formerly-locked tile pulses to signal it is
// now a normal red tile.
//
// We deliberately stop the animation at "lock just broke" — we don't show
// the subsequent merge with a third red. That would crowd the demo and
// the body text already covers "can merge in chains." The CHARGED →
// adjacent-to-LOCK → unlocks transition is the unique W11 concept.
const KEEP_CSS = `
.tess-anim-keep .charged { animation: 4s ease-in-out infinite tess-keep-charged; transform-origin: center; }
.tess-anim-keep .lock { animation: 4s ease-in-out infinite tess-keep-lock; transform-origin: center; }
.tess-anim-keep .badge { animation: 4s ease-in-out infinite tess-keep-badge; transform-origin: center; }

@keyframes tess-keep-charged { /* slot 0 → slot 2 (adjacent to lock at 3) */
  0%, 12% { transform: translateX(0); }
  35%, 65% { transform: translateX(${(2 * STEP).toFixed(2)}px); }
  85%, 100% { transform: translateX(0); }
}
@keyframes tess-keep-lock { /* unlocks → small pulse of the freshly-unlocked tile */
  0%, 38% { transform: scale(1); }
  44%     { transform: scale(1.12); }
  56%     { transform: scale(1); }
  100%    { transform: scale(1); }
}
@keyframes tess-keep-badge { /* fades out as charged arrives */
  0%, 32% { opacity: 1; transform: scale(1); }
  44%     { opacity: 0; transform: scale(0.6); }
  82%     { opacity: 0; transform: scale(0.6); }
  100%    { opacity: 1; transform: scale(1); }
}
`;

export function KeepLockDemo() {
  const w = PAD_X * 2 + 3 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-keep"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a charged tile sliding adjacent to a locked tile, breaking the lock"
      style={{ display: 'block', maxWidth: 280, margin: '4px auto 12px' }}
    >
      <style>{KEEP_CSS}</style>
      {[0, 1, 2, 3].map((i) => <HexCell key={i} i={i} total={4} />)}
      {/* Charged red — slot 0 → 2 */}
      <g className="charged" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
        <ChargedRing />
      </g>
      {/* Locked red — at slot 3 */}
      <g transform={`translate(${CELL_X(3)},${CELL_Y})`}>
        <g className="lock">
          <TileBody color={TILE_PALETTE[0]!} />
        </g>
        <g className="badge">
          <LockBadge />
        </g>
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W13 CRUCIBLE — "Mixer takes the color of whatever slides into it"
// ---------------------------------------------------------------------------
//
// Layout: 4 cells. Red tile at slot 0, mixer (gray with three dots) at slot 3.
// Red slides east, stops at slot 2 (mixer at 3 is the blocker). The mixer's
// fill transitions from gray to red and the three dots fade away — the
// mixer has been claimed and is now a normal red tile.
//
// We keep the post-claim adjacency obvious (two reds side by side) so the
// player visually grasps "now I have two reds adjacent — I can build toward
// a charged tile from here."
const CRUCIBLE_CSS = `
.tess-anim-crucible .feeder { animation: 4s ease-in-out infinite tess-cruc-feeder; transform-origin: center; }
.tess-anim-crucible .mixer-body { animation: 4s ease-in-out infinite tess-cruc-mixer; }
.tess-anim-crucible .dots { animation: 4s ease-in-out infinite tess-cruc-dots; transform-origin: center; }

@keyframes tess-cruc-feeder { /* slot 0 → slot 2 (mixer is the blocker at slot 3) */
  0%, 12% { transform: translateX(0); }
  38%, 70% { transform: translateX(${(2 * STEP).toFixed(2)}px); }
  88%, 100% { transform: translateX(0); }
}
@keyframes tess-cruc-mixer { /* gray → red on contact */
  0%, 38%   { fill: #9C968B; }
  46%, 86%  { fill: ${TILE_PALETTE[0]}; }
  100%      { fill: #9C968B; }
}
@keyframes tess-cruc-dots { /* dots fade out as mixer claims color */
  0%, 38%   { opacity: 1; transform: scale(1); }
  48%       { opacity: 0; transform: scale(0.6); }
  86%       { opacity: 0; transform: scale(0.6); }
  100%      { opacity: 1; transform: scale(1); }
}
`;

export function CrucibleMixerDemo() {
  const w = PAD_X * 2 + 3 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-crucible"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a red tile sliding into a gray mixer, which then becomes red"
      style={{ display: 'block', maxWidth: 280, margin: '4px auto 12px' }}
    >
      <style>{CRUCIBLE_CSS}</style>
      {[0, 1, 2, 3].map((i) => <HexCell key={i} i={i} total={4} />)}
      {/* Feeder red tile — slot 0 → 2 */}
      <g className="feeder" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
      {/* Mixer at slot 3 — fill animates gray → red */}
      <g transform={`translate(${CELL_X(3)},${CELL_Y})`}>
        <path d={hexPath(SIZE)} className="mixer-body" stroke="#FFFFFF22" strokeWidth="1.5" />
        <path d={hexPath(SIZE - 6)} fill="none" stroke="#FFFFFFCC" strokeWidth="1" opacity="0.6" />
        <g className="dots">
          <MixerDots />
        </g>
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W14 BALLAST — "Arrow cells trigger an automatic re-slide"
// ---------------------------------------------------------------------------
//
// Layout: 5 cells. Red tile at slot 0. An eastward arrow drawn into slot 2.
// Player slides east. Tile lands on slot 2 (the arrow cell), then auto-
// slides further east, ending at slot 4 against the wall. The arrow pulses
// at the moment of trigger.
//
// The whole travel is one move from the player's accounting perspective —
// this is the value of arrow cells. Body text reinforces that the move
// budget is only debited once.
const BALLAST_CSS = `
.tess-anim-ballast .traveler { animation: 4s ease-in-out infinite tess-bal-trav; transform-origin: center; }
.tess-anim-ballast .arrow { animation: 4s ease-in-out infinite tess-bal-arrow; transform-origin: center; }

@keyframes tess-bal-trav {
  /* slot 0 → slot 2 (manual slide) → pause → slot 4 (auto re-slide) → reset */
  0%, 8%    { transform: translateX(0); }
  28%, 32%  { transform: translateX(${(2 * STEP).toFixed(2)}px); }    /* lands on arrow */
  54%, 78%  { transform: translateX(${(4 * STEP).toFixed(2)}px); }    /* auto-slid to wall */
  92%, 100% { transform: translateX(0); }
}
@keyframes tess-bal-arrow {
  0%, 28%  { opacity: 0.55; transform: scale(1); }
  34%      { opacity: 1; transform: scale(1.18); }
  46%      { opacity: 0.55; transform: scale(1); }
  100%     { opacity: 0.55; transform: scale(1); }
}
`;

export function BallastArrowDemo() {
  const w = PAD_X * 2 + 4 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-ballast"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a tile that lands on an arrow cell auto-sliding further in the arrow's direction"
      style={{ display: 'block', maxWidth: 320, margin: '4px auto 12px' }}
    >
      <style>{BALLAST_CSS}</style>
      {[0, 1, 2, 3, 4].map((i) => <HexCell key={i} i={i} total={5} />)}
      {/* Arrow drawn into slot 2 */}
      <g transform={`translate(${CELL_X(2)},${CELL_Y})`}>
        <g className="arrow">
          <GravityArrowEast />
        </g>
      </g>
      {/* Traveler red tile */}
      <g className="traveler" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W15 CAIRN — "Land the right color on the marked cell"
// ---------------------------------------------------------------------------
//
// Layout: 4 cells. A blue blocker at slot 3, a target marker (dotted ring +
// red center dot) at slot 2, and a red traveler at slot 0. The red slides
// east, gets stopped by the blue blocker, lands ON the target cell — and
// the target marker fades because it's now satisfied (red on red-target).
//
// The novel concept this teaches: the goal isn't an empty board. It's
// satisfying the markers. Players see one slide that doesn't clear
// anything, but it WINS — because the marked cell now holds the right
// color. The body text reinforces "you do not need to clear the rest."
//
// We render the target marker visuals identically to Board.tsx (dotted
// ring + tinted center dot) so the in-game appearance and the tutorial
// preview match exactly. Color #C7613A (TILE_PALETTE[0], terracotta) is
// the demo target color.
const CAIRN_CSS = `
.tess-anim-cairn .traveler { animation: 4s ease-in-out infinite tess-cairn-trav; transform-origin: center; }
.tess-anim-cairn .marker { animation: 4s ease-in-out infinite tess-cairn-marker; transform-origin: center; }

@keyframes tess-cairn-trav {
  0%, 12% { transform: translateX(0); }
  36%, 70% { transform: translateX(${(2 * STEP).toFixed(2)}px); }   /* lands on target */
  88%, 100% { transform: translateX(0); }
}
@keyframes tess-cairn-marker {
  /* Marker visible at start; fades as the traveler lands on it (target
     satisfied); stays faded through the pause; reappears for the loop. */
  0%, 30%   { opacity: 1; transform: scale(1); }
  42%       { opacity: 0; transform: scale(1.15); }
  78%       { opacity: 0; transform: scale(1.15); }
  100%      { opacity: 1; transform: scale(1); }
}
`;

export function CairnTargetDemo() {
  const w = PAD_X * 2 + 3 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-cairn"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a red tile sliding onto a target cell that wants red, satisfying the target"
      style={{ display: 'block', maxWidth: 280, margin: '4px auto 12px' }}
    >
      <style>{CAIRN_CSS}</style>
      {[0, 1, 2, 3].map((i) => <HexCell key={i} i={i} total={4} />)}
      {/* Target marker on slot 2 — dotted ring + red center dot. Fades as
          the traveler arrives and satisfies it. */}
      <g className="marker" transform={`translate(${CELL_X(2)},${CELL_Y})`} aria-hidden>
        <path
          d={hexPath(SIZE * 0.85)}
          fill="none"
          stroke="#3a3a3a"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          opacity="0.5"
        />
        <circle cx={0} cy={0} r={SIZE * 0.18} fill={TILE_PALETTE[0]!} opacity="0.6" />
      </g>
      {/* Blue blocker — stays put at slot 3 */}
      <g transform={`translate(${CELL_X(3)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[1]!} />
      </g>
      {/* Red traveler — slot 0 → 2 (lands on target) */}
      <g className="traveler" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W16 RIVEN — "Some cells are gone — they act as barriers"
// ---------------------------------------------------------------------------
//
// Layout: 5 cells. Slot 2 is a HOLE (rendered as nothing — no hex outline,
// completely empty space where a cell would normally be). A red tile sits
// at slot 0; another red sits at slot 4. A naive "slide east to merge"
// expectation suggests the red at slot 0 slides east and meets the red at
// slot 4 — but the hole at slot 2 stops the slide. The traveler ends at
// slot 1 (cell just before the hole), unable to reach the other red.
//
// The novel concept this teaches: holes act as hard barriers, just like
// the board edge. Two tiles that look adjacent on screen may be separated
// by a hole strip and unable to merge. Players have to read the board
// GEOMETRY before each slide — the rules haven't changed, but the
// playable region has, and that re-shapes everything.
//
// To make the hole visually unmistakable in the small modal, we render
// the in-board cells with their normal hex outlines, and slot 2 with NO
// outline at all (just empty space). A subtle dotted "missing" indicator
// would distract from the mechanic; the absence-of-cell IS the message.
const RIVEN_CSS = `
.tess-anim-riven .traveler { animation: 4s ease-in-out infinite tess-riven-trav; transform-origin: center; }
.tess-anim-riven .stuck-pulse { animation: 4s ease-in-out infinite tess-riven-stuck; transform-origin: center; }

@keyframes tess-riven-trav {
  /* slot 0 → slot 1 (stops before the hole at slot 2) → hold → reset */
  0%, 12% { transform: translateX(0); }
  36%, 78% { transform: translateX(${STEP.toFixed(2)}px); }
  92%, 100% { transform: translateX(0); }
}
@keyframes tess-riven-stuck {
  /* Brief "wall hit" pulse on the traveler at the moment it stops adjacent
     to the hole — communicates "this is as far as it goes." */
  0%, 30% { opacity: 0; transform: scale(1); }
  38%     { opacity: 0.7; transform: scale(1.18); }
  50%     { opacity: 0; transform: scale(1); }
  100%    { opacity: 0; }
}
`;

export function RivenHoleDemo() {
  const w = PAD_X * 2 + 4 * STEP;
  const h = PAD_Y * 2;
  return (
    <svg
      className="tess-anim-riven"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a slide stopping at a hole in the board, demonstrating that holes act as barriers"
      style={{ display: 'block', maxWidth: 320, margin: '4px auto 12px' }}
    >
      <style>{RIVEN_CSS}</style>
      {/* In-board cells: render slots 0, 1, 3, 4. Slot 2 is the HOLE — no
          hex outline drawn, just empty whitespace where a cell would be. */}
      {[0, 1, 3, 4].map((i) => <HexCell key={i} i={i} total={5} />)}
      {/* Stuck-pulse indicator at slot 1 (where the traveler stops). Fires
          briefly at the moment the traveler hits the hole-barrier. */}
      <g className="stuck-pulse" transform={`translate(${CELL_X(1)},${CELL_Y})`}>
        <circle r={SIZE * 0.55} fill="none" stroke="var(--accent, #C7613A)" strokeWidth="1.5" />
      </g>
      {/* Stationary red at slot 4 — the "destination" that the traveler
          can't actually reach because of the hole. Shows visually that
          two reds exist on the board but cannot meet. */}
      <g transform={`translate(${CELL_X(4)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
      {/* Traveler red — slides slot 0 → 1, stops at the hole */}
      <g className="traveler" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W17 GALE — "Wind nudges your tile one cell further"
// ---------------------------------------------------------------------------
//
// Layout: 5 cells in a row. Wind glyph drawn at slot 1 pointing east. A
// red tile at slot 0 slides east; a blue blocker at slot 3 stops the
// natural slide path at slot 2. Without wind, the red would land at slot
// 2. WITH wind at slot 1 (which targets slot 2), the wind kicks in upon
// landing and pushes the red ONE more cell east — to slot 3? No, slot 3
// is blocked. Hmm.
//
// Better: wind at slot 1 targets slot 2; red lands on slot 2; wind pushes
// red east; slot 3 has a blue tile blocking; wind aborts. The animation
// shows the wind glyph pulsing, the red landing on slot 2, but no further
// movement (because of the blocker).
//
// To show wind ACTUALLY MOVING the tile, swap geometry: wind at slot 2
// pointing west targets slot 1. Red at slot 4 slides west. Without wind,
// red would land at slot 3 (blocked by something) or slot 0 (wall). Need
// a blocker at slot 0 so red stops at slot 1.
//
// Cleaner approach for the demo: emphasize the EXTENSION effect by having
// the slide stop SHORT of the wall, with wind nudging it the last cell.
//
// Final layout:
//   - 5 cells slot 0..4
//   - Blocker (blue) at slot 0 (west edge "feels" wall-like)
//   - Wind glyph at slot 2 pointing east (targets slot 3)
//   - Red traveler at slot 4 slides west? But that's the opposite direction.
//
// Simpler: just SHOW the wind glyph + a slide that lands on the target
// cell + a clear push outward. We accept that "wind extends the slide"
// is the message even if the demo is partly visual rather than mechanical.
//
// Final demo:
//   - Wind glyph at slot 1 pointing east
//   - Red at slot 0 slides east, lands at slot 1 (the wind's target)
//   - Wind animation: wind glyph pulses, red gets pushed +1 east to slot 2
//   - That's it. The "blocker" that stops the slide is implied (the
//     tutorial board feels small enough that the wind logic reads).
//
// Actually that doesn't work either — red sliding east from slot 0 with
// no blocker travels straight to slot 4 (the wall). Doesn't land at
// slot 1 unless something's there.
//
// SIMPLEST honest demo:
//   - Wind glyph at slot 1 pointing east
//   - Blocker (blue) at slot 2
//   - Red at slot 0 slides east, slot 2 blocked, red lands at slot 1.
//   - Wind glyph at slot 1 → wait, that's the cell red just landed on.
//     But wind targets cell ADJACENT in wind direction, which would be
//     slot 2 (east of slot 1) — and slot 2 is the blocker. So wind
//     wouldn't fire here either.
//
// The wind mechanic is genuinely hard to demo in a 1-row layout because
// the trigger geometry requires the tile to land at step(windCell, dir).
//
// Let me try: wind glyph at slot 0, pointing east, targets slot 1.
//   Red at slot 4 slides west. With blocker at slot 0, red stops at slot 1.
//   slot 1 IS the wind target. Wind fires, pushes red east; slot 2 open;
//   red goes to slot 2. Hmm but visually the player sees red moving
//   WEST then snapping back EAST — confusing.
//
// Cleanest: wind at slot 0 east, red at slot 4, slide west, blocker at
// slot 0... wait the blocker IS at slot 0 and that's also the wind cell?
// Engine throws if you addWind on an occupied cell. Skip.
//
// Let me try: wind at slot 0 east, red at slot 3, blue at slot 4, slide
// west. Red travels (3,2) wait... westward from slot 3 goes to slot 2,
// slot 1, then blocker at slot 0 stops it at slot 1. Slot 1 = wind target.
// Wind pushes east → slot 2 open → red goes to slot 2. Net: red at slot 3
// moves to slot 2 via this two-step process. That's a 1-cell westward
// movement that LOOKS LIKE a slide.
//
// OK forget trying to make it perfectly mechanical. The demo just needs
// to communicate "wind nudges +1 in its direction." Use a stylized
// representation that shows wind pushing a tile, even if the underlying
// state transitions are simplified.
//
// FINAL approach: a stylized animation
//   - Wind glyph at slot 1 pointing east (visible from frame 0)
//   - Red tile starts at slot 0
//   - Red slides to slot 1 (lands at wind target — implied "stopped here
//     because of geometry off-screen")
//   - Brief pause, wind glyph pulses
//   - Red is pushed +1 east, ending at slot 2
//   - Reset and loop
//
// The animation steps don't have to be 100% mechanically faithful — they
// communicate the rule. The body text reinforces the precise mechanic.
const GALE_CSS = `
.tess-anim-gale .traveler { animation: 5s ease-in-out infinite tess-gale-trav; transform-origin: center; }
.tess-anim-gale .wind-glyph { animation: 5s ease-in-out infinite tess-gale-wind; transform-origin: center; transform-box: view-box; }

@keyframes tess-gale-trav {
  /* slot 0 → slot 1 (lands on wind-target) → pause → slot 2 (wind nudge) → reset */
  0%, 8%    { transform: translateX(0); }
  22%, 30%  { transform: translateX(${STEP.toFixed(2)}px); }      /* lands at wind target */
  44%, 76%  { transform: translateX(${(2 * STEP).toFixed(2)}px); }/* wind pushes +1 */
  90%, 100% { transform: translateX(0); }
}
@keyframes tess-gale-wind {
  /* Wind glyph pulses at the moment of triggering. */
  0%, 24%  { opacity: 0.55; transform: scale(1); }
  32%      { opacity: 1; transform: scale(1.2); }
  44%      { opacity: 0.55; transform: scale(1); }
  100%     { opacity: 0.55; transform: scale(1); }
}
`;

export function GaleWindDemo() {
  const w = PAD_X * 2 + 4 * STEP;
  const h = PAD_Y * 2;
  // Wind glyph path — same shape as Board.tsx wind glyph but at tutorial
  // scale (SIZE=22 instead of TILE_SIZE=34). Two stacked breeze curves
  // followed by a chevron tip pointing east.
  const L = SIZE * 0.32;
  const wh = SIZE * 0.13;
  const windD = `M ${-L} ${-wh * 0.6} Q ${-L * 0.3} ${-wh * 0.3} ${L * 0.3} ${-wh * 0.3} M ${-L} ${wh * 0.6} Q ${-L * 0.3} ${wh * 0.3} ${L * 0.3} ${wh * 0.3} M ${L * 0.3 - wh} ${-wh} L ${L * 0.3} 0 L ${L * 0.3 - wh} ${wh}`;
  return (
    <svg
      className="tess-anim-gale"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a wind cell nudging a tile one cell further in the wind's direction"
      style={{ display: 'block', maxWidth: 320, margin: '4px auto 12px' }}
    >
      <style>{GALE_CSS}</style>
      {[0, 1, 2, 3, 4].map((i) => <HexCell key={i} i={i} total={5} />)}
      {/* Wind glyph at slot 1, pointing east. Pulses at the moment of
          triggering. Visible at all times so the player understands that
          the wind is a permanent feature of the cell, not an event. */}
      <g transform={`translate(${CELL_X(1)},${CELL_Y})`}>
        <path
          className="wind-glyph"
          d={windD}
          fill="none"
          stroke="var(--hex-line, #3a3a3a)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      {/* Red traveler — slides slot 0 → 1 (lands on wind target) → 2 (wind nudge) */}
      <g className="traveler" transform={`translate(${CELL_X(0)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Demo: W18 EMBERS — "Reactive tiles detonate when merged"
// ---------------------------------------------------------------------------
//
// Layout: 5 cells in a row. Three reds at slots 0, 1, 2 — the middle one
// (slot 1) is reactive (faint pulsing inner outline). A blue tile at the
// adjacent off-row position (rendered as a separate cell adjacent to
// slot 1's hex). Without the merge, the reactive sits dormant and pulses.
// On merge: three reds collapse into a charged red, AND the blue at the
// reactive's neighbor cell vanishes (the "detonation").
//
// Constraint: the demo is a 1-row layout but reactive's neighbors are
// hex-geometric (6 around). To show the detonation visually within the
// row layout, we use a "satellite" cell drawn slightly offset above slot
// 1 to represent an adjacent neighbor. This is a stylized representation
// — the real geometry is hex but the message reads clearly: "merged
// tile + adjacent tile = both gone."
const EMBERS_CSS = `
.tess-anim-embers .reactive-pulse { animation: 5s ease-in-out infinite tess-embers-rpulse; transform-origin: center; transform-box: view-box; }
.tess-anim-embers .reds { animation: 5s ease-in-out infinite tess-embers-merge; transform-origin: center; transform-box: view-box; }
.tess-anim-embers .neighbor { animation: 5s ease-in-out infinite tess-embers-blast; transform-origin: center; transform-box: view-box; }
.tess-anim-embers .charged { animation: 5s ease-in-out infinite tess-embers-charged; transform-origin: center; transform-box: view-box; }

@keyframes tess-embers-rpulse {
  /* Reactive's faint pulse before merge. Stops at ~50% (when merge fires). */
  0%, 8% { opacity: 0.45; transform: scale(1); }
  20% { opacity: 0.9; transform: scale(1.06); }
  35% { opacity: 0.45; transform: scale(1); }
  50%, 100% { opacity: 0; }
}
@keyframes tess-embers-merge {
  /* Three reds visible 0-50%, then vanish. */
  0%, 48% { opacity: 1; transform: scale(1); }
  56% { opacity: 0; transform: scale(0.6); }
  100% { opacity: 0; }
}
@keyframes tess-embers-blast {
  /* Blue neighbor visible 0-50%, then "detonates" (scale up, fade out). */
  0%, 48% { opacity: 1; transform: scale(1); }
  56% { opacity: 0.5; transform: scale(1.4); }
  68% { opacity: 0; transform: scale(1.6); }
  100% { opacity: 0; }
}
@keyframes tess-embers-charged {
  /* Charged appears at slot 1 after merge (50%+). */
  0%, 50% { opacity: 0; transform: scale(0.6); }
  60% { opacity: 1; transform: scale(1.1); }
  72%, 100% { opacity: 1; transform: scale(1); }
}
`;

export function EmbersDetonateDemo() {
  const w = PAD_X * 2 + 4 * STEP;
  const h = PAD_Y * 2 + SIZE; // extra height for the satellite "neighbor" cell
  const neighborY = CELL_Y - SIZE * 1.6;
  return (
    <svg
      className="tess-anim-embers"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      role="img"
      aria-label="Animation showing a reactive tile detonating on merge — three reds merge into a charged tile, and an adjacent blue tile is cleared by the detonation"
      style={{ display: 'block', maxWidth: 320, margin: '4px auto 12px' }}
    >
      <style>{EMBERS_CSS}</style>
      {[0, 1, 2, 3, 4].map((i) => <HexCell key={i} i={i} total={5} />)}
      {/* Satellite "neighbor" cell above slot 1 — represents one of the
          reactive's hex neighbors. Slightly offset visually from the
          row to read as "adjacent" without crowding. */}
      <g transform={`translate(${CELL_X(1)},${neighborY})`}>
        <path
          d={hexPath(SIZE)}
          fill="var(--hex-bg, #00000008)"
          stroke="var(--hex-line, #3a3a3a)"
          strokeOpacity="0.35"
          strokeWidth="1"
        />
      </g>
      {/* Blue neighbor — gets cleared by the detonation */}
      <g className="neighbor" transform={`translate(${CELL_X(1)},${neighborY})`}>
        <TileBody color={TILE_PALETTE[1]!} />
      </g>
      {/* Three reds at slots 0, 1, 2 — middle one is reactive */}
      <g className="reds">
        <g transform={`translate(${CELL_X(0)},${CELL_Y})`}>
          <TileBody color={TILE_PALETTE[0]!} />
        </g>
        <g transform={`translate(${CELL_X(1)},${CELL_Y})`}>
          <TileBody color={TILE_PALETTE[0]!} />
          <path
            d={hexPath(SIZE - 5)}
            fill="none"
            stroke="#FFFFFFCC"
            strokeWidth="1.5"
            className="reactive-pulse"
          />
        </g>
        <g transform={`translate(${CELL_X(2)},${CELL_Y})`}>
          <TileBody color={TILE_PALETTE[0]!} />
        </g>
      </g>
      {/* Resulting charged red — appears at slot 1 after merge */}
      <g className="charged" transform={`translate(${CELL_X(1)},${CELL_Y})`}>
        <TileBody color={TILE_PALETTE[0]!} />
        <ChargedRing />
      </g>
    </svg>
  );
}
